import * as Sentry from '@sentry/react';
import { noteExpectedFailure } from '@utils/observability/expectedFailure';
import { isAlarmSoundUrl } from '@utils/sounds';

// `HTMLMediaElement.play()` returns a promise that rejects for three very different reasons, and
// every raw `audio.play()` in this app handed those rejections to Sentry's onunhandledrejection
// hook instead of dealing with them: PLICK-FRONT-PANEL-COMERCIO-173 and -127 (NotAllowedError on
// iOS, 550 events / ~73 commerces) and -64 (NotSupportedError, 1434 events).
//
// The classification happens here, at the source, by DOMException `name`, which is standard
// contract. Adding message patterns to `ignoreErrors` was tried and failed twice: the three regexes
// there are Chrome/Firefox wordings and match neither Safari's autoplay text nor NotSupportedError,
// and that wording changes with every engine release.
const EXPECTED_PLAY_FAILURES = new Set([
  // Autoplay policy. The unlock effect in NotificationsContext gives an alternate path to every
  // commerce that touches the panel at least once. It gives NONE to the one that never does, which
  // is why the breadcrumb is not the whole answer here: see reportAlarmBlockedWithoutUnlock below.
  'NotAllowedError',
  // `stopSound()` paused a play() that was still in flight. Only pause() can produce this now: the
  // other producer, a src change racing its own play(), was removed by waiting for `canplay` in the
  // resync effect — the two are indistinguishable by name, so the race had to go instead.
  'AbortError',
]);

/**
 * Plays a media element without ever leaking an unhandled rejection, and without silencing a real
 * failure: anything that is not browser-contract noise still reaches Sentry with the culprit `src`.
 *
 * @param {HTMLAudioElement} audio element to play
 * @param {Function} [onSettled] runs once the play attempt settles, receiving the rejection reason
 *   when it failed. The autoplay-unlock effect uses it to know whether the priming actually worked.
 * @returns {void}
 */
export const safePlay = (audio: HTMLAudioElement, onSettled?: (error?: unknown) => void): void => {
  const settle = (error?: unknown) => {
    if (error) {
      const name = (error as { name?: string })?.name || 'UnknownError';
      const src = audio.currentSrc || audio.src;
      if (EXPECTED_PLAY_FAILURES.has(name)) {
        noteExpectedFailure('audio', 'alarm play rejected by the browser', { name, src });
      } else {
        // Canary. With the stored preference normalized by resolveAlarmSound(), a NotSupportedError
        // here means a real commerce lost its order alarm for a reason we have not seen yet, and we
        // need the `src` to know which.
        Sentry.captureException(error, {
          tags: { audio_failure: name },
          extra: { src, media_error_code: audio.error?.code ?? null, in_catalog: isAlarmSoundUrl(src) },
        });
      }
    }
    onSettled?.(error);
  };

  let result: unknown;
  try {
    result = audio.play();
  } catch (error) {
    // Some engines throw synchronously instead of returning a rejected promise.
    settle(error);
    return;
  }
  if (result && typeof (result as Promise<void>).then === 'function') {
    (result as Promise<void>).then(() => settle(), settle);
    return;
  }
  // Pre-promise engines (and the jsdom mocks in the specs) return undefined: treat that as success.
  settle();
};

/**
 * Reports a source that the media element could not load at all.
 *
 * Waiting for `canplay` before playing removes the src-vs-play race, but it also removes the signal
 * that came with it: an unplayable source used to reject play() with NotSupportedError, and a
 * `canplay` that never fires is silent. This keeps the canary of PLICK-FRONT-PANEL-COMERCIO-64
 * alive on that path.
 *
 * @param {HTMLAudioElement} audio element whose source failed to load
 * @returns {void}
 */
export const reportUnplayableAlarmSource = (audio: HTMLAudioElement): void => {
  const src = audio.currentSrc || audio.src;
  Sentry.captureException(new Error('alarm sound source failed to load'), {
    tags: { audio_failure: 'MediaLoadError' },
    extra: { src, media_error_code: audio.error?.code ?? null, in_catalog: isAlarmSoundUrl(src) },
  });
};

/**
 * Reports that an order alarm was blocked by the autoplay policy on a panel whose playback had
 * never been unlocked. Returns whether the event was actually emitted, so the caller can keep it to
 * one per session.
 *
 * Why this stays an EVENT while every other expected play failure became a breadcrumb: a breadcrumb
 * only travels attached to a later error, and the session this measures has no later error — the
 * panel is a wall-mounted tablet nobody touches, the gesture the unlock effect waits for never
 * arrives, and the alarm is mute from the first order to the last with nobody noticing. Downgrading
 * this one to a breadcrumb would not have removed noise, it would have removed the thermometer:
 * PLICK-FRONT-PANEL-COMERCIO-173/-127 WERE this population (the `ignoreErrors` regexes in index.tsx
 * are Chrome/Firefox wordings, so what reached Sentry was WebKit). One warning per affected session
 * keeps the commerce count without bringing back the per-order volume.
 *
 * @param {unknown} error rejection reason from `play()`
 * @returns {boolean} true when an event was emitted (i.e. the rejection was the autoplay policy)
 */
export const reportAlarmBlockedWithoutUnlock = (error: unknown): boolean => {
  if ((error as { name?: string })?.name !== 'NotAllowedError') return false;
  try {
    Sentry.captureMessage('order alarm blocked by autoplay policy, playback never unlocked', {
      level: 'warning',
      // Scoped to THIS event, never on the global scope: a global fingerprint would re-group every
      // later error of the session under this one.
      fingerprint: ['audio', 'autoplay-locked-no-unlock'],
      tags: { audio_failure: 'NotAllowedError' },
    });
  } catch {
    // Observability must never break the alarm it observes: this runs inside a promise callback, so
    // a throw here would be exactly the unhandled rejection this file exists to remove.
    return false;
  }
  return true;
};
